<template>
  <div class="tw:w-full tw:rounded-lg tw:border tw:border-(--divider-color)" style="overflow: visible;">
    <table class="tw:w-full tw:border-collapse tw:text-sm">
      <!-- Header -->
      <thead>
        <tr class="tw:border-b tw:border-(--divider-color)">
          <th
            v-for="(col, colIdx) in columns"
            :key="col.key"
            :style="col.width ? { width: col.width } : undefined"
            :class="[
              'tw:px-4 tw:py-3 tw:font-semibold tw:text-xs tw:uppercase tw:tracking-wide tw:text-(--text-color) tw:bg-(--surface-color)',
              colIdx === 0 && 'tw:rounded-tl-lg',
              colIdx === columns.length - 1 && 'tw:rounded-tr-lg',
              alignClass(col.align),
              col.sortable && 'tw:cursor-pointer tw:select-none hover:tw:text-(--fontcolor)',
            ]"
            @click="col.sortable && toggleSort(col.key)"
          >
            <slot :name="`header-${col.key}`" :column="col">
              <div class="tw:flex tw:items-center tw:gap-1" :class="alignFlexClass(col.align)">
                <span>{{ col.label }}</span>
                <IconGoogle
                  v-if="col.sortable && sortKey === col.key"
                  :name="sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'"
                  size="14px"
                  color="var(--fontcolor)"
                />
              </div>
            </slot>
          </th>
        </tr>
      </thead>

      <!-- Body -->
      <tbody v-if="!loading && sortedData.length > 0">
        <tr
          v-for="(row, index) in sortedData"
          :key="index"
          :class="[
            'table-play__row tw:border-b tw:border-(--divider-color) tw:last:border-b-0 tw:transition-colors tw:duration-100',
            hoverable && 'table-play__row--hoverable',
            clickableRows && 'tw:cursor-pointer',
            striped && index % 2 === 1 && 'tw:bg-(--surface-color)/50',
          ]"
          @click="clickableRows && emit('row-click', row, index)"
        >
          <td
            v-for="col in columns"
            :key="col.key"
            :class="['tw:px-4 tw:py-3 tw:text-(--fontcolor)', alignClass(col.align)]"
          >
            <slot :name="col.key" :row="row" :value="row[col.key]" :index="index">
              <TextPlay :text="String(row[col.key] ?? '-')" size="sm" />
            </slot>
          </td>
        </tr>
      </tbody>

      <!-- Loading skeleton -->
      <tbody v-else-if="loading">
        <tr v-for="i in skeletonRows" :key="i" class="tw:border-b tw:border-(--divider-color) tw:last:border-b-0">
          <td v-for="col in columns" :key="col.key" class="tw:px-4 tw:py-3">
            <div class="tw:h-4 tw:rounded skeleton-play" :style="{ width: col.width || '80%' }" />
          </td>
        </tr>
      </tbody>

      <!-- Empty state -->
      <tbody v-else>
        <tr>
          <td :colspan="columns.length" class="tw:px-4 tw:py-12 tw:text-center">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2">
              <IconGoogle name="inbox" size="2rem" color="var(--text-color)" />
              <TextPlay :text="emptyText" size="sm" color="var(--text-color)" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

export interface TableColumn {
  key: string
  label: string
  width?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

const props = withDefaults(defineProps<{
  columns: TableColumn[]
  data: Record<string, unknown>[]
  loading?: boolean
  emptyText?: string
  striped?: boolean
  hoverable?: boolean
  clickableRows?: boolean
  skeletonRows?: number
}>(), {
  loading: false,
  emptyText: 'Nenhum dado encontrado',
  striped: false,
  hoverable: true,
  clickableRows: false,
  skeletonRows: 5,
})

const emit = defineEmits<{
  'row-click': [row: Record<string, unknown>, index: number]
}>()

const sortKey = ref<string | null>(null)
const sortOrder = ref<'asc' | 'desc'>('asc')

function toggleSort(key: string) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedData = computed(() => {
  if (!sortKey.value) return props.data
  const key = sortKey.value
  const order = sortOrder.value === 'asc' ? 1 : -1
  return [...props.data].sort((a, b) => {
    const valA = a[key] ?? ''
    const valB = b[key] ?? ''
    if (valA < valB) return -1 * order
    if (valA > valB) return 1 * order
    return 0
  })
})

function alignClass(align?: string) {
  if (align === 'center') return 'tw:text-center'
  if (align === 'right') return 'tw:text-right'
  return 'tw:text-left'
}

function alignFlexClass(align?: string) {
  if (align === 'center') return 'tw:justify-center'
  if (align === 'right') return 'tw:justify-end'
  return 'tw:justify-start'
}
</script>

<style scoped>
.table-play__row--hoverable:hover {
  background-color: var(--box-background-color);
}
</style>
